import { useState } from 'react';
import { Shield, Archive, Check, X, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function ApiTest() {
  // État pour 2FA
  const [twoFactorQr, setTwoFactorQr] = useState('');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [loading2FA, setLoading2FA] = useState(false);

  // État pour Archive
  const [projectId, setProjectId] = useState('');
  const [loadingArchive, setLoadingArchive] = useState(false);

  // Test 2FA Setup
  const handleSetup2FA = async () => {
    setLoading2FA(true);
    try {
      const response = await api.post('/2fa/setup');
      setTwoFactorQr(response.data.qrCode);
      setTwoFactorSecret(response.data.secret);
      toast.success('✅ 2FA Setup réussi');
    } catch (error) {
      console.error('Erreur 2FA setup:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du setup 2FA');
    } finally {
      setLoading2FA(false);
    }
  };

  // Test 2FA Verify
  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }

    setLoading2FA(true);
    try {
      await api.post('/2fa/verify', { token: verificationCode });
      toast.success('✅ 2FA vérifié et activé');
      setVerificationCode('');
    } catch (error) {
      console.error('Erreur 2FA verify:', error);
      toast.error(error.response?.data?.message || 'Code invalide');
    } finally {
      setLoading2FA(false);
    }
  };

  // Test 2FA Disable
  const handleDisable2FA = async () => {
    if (!disablePassword.trim()) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }

    if (!window.confirm('Désactiver l\'authentification à deux facteurs ?')) return;

    setLoading2FA(true);
    try {
      await api.post('/2fa/disable', { password: disablePassword });
      toast.success('✅ 2FA désactivé');
      setTwoFactorQr('');
      setTwoFactorSecret('');
      setDisablePassword('');
    } catch (error) {
      console.error('Erreur 2FA disable:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la désactivation');
    } finally {
      setLoading2FA(false);
    }
  };

  // Test Archive Project
  const handleArchiveProject = async () => {
    if (!projectId.trim()) {
      toast.error('Veuillez entrer un ID de projet');
      return;
    }

    setLoadingArchive(true);
    try {
      await api.post(`/projects/${projectId}/archive`);
      toast.success('✅ Projet archivé avec succès');
      setProjectId('');
    } catch (error) {
      console.error('Erreur archivage projet:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'archivage');
    } finally {
      setLoadingArchive(false);
    }
  };

  // Test Unarchive Project
  const handleUnarchiveProject = async () => {
    if (!projectId.trim()) {
      toast.error('Veuillez entrer un ID de projet');
      return;
    }

    setLoadingArchive(true);
    try {
      await api.post(`/projects/${projectId}/unarchive`);
      toast.success('✅ Projet désarchivé avec succès');
      setProjectId('');
    } catch (error) {
      console.error('Erreur désarchivage projet:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du désarchivage');
    } finally {
      setLoadingArchive(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">🧪 Tests API</h1>
          <p className="text-gray-600">Page de test pour les fonctionnalités 2FA et Archivage de projet</p>
        </div>

        {/* Section 2FA */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Authentification à deux facteurs (2FA)</h2>
          </div>

          <div className="space-y-4">
            {/* Setup 2FA */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">1. Configuration 2FA</h3>
              <p className="text-sm text-gray-600 mb-3">
                <code className="bg-gray-100 px-2 py-1 rounded">POST /api/2fa/setup</code>
              </p>
              <button
                onClick={handleSetup2FA}
                disabled={loading2FA}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {loading2FA ? 'Chargement...' : 'Générer QR Code'}
              </button>

              {twoFactorQr && (
                <div className="mt-4 space-y-3">
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-4 flex justify-center">
                    <img src={twoFactorQr} alt="QR Code 2FA" className="w-48 h-48" />
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Secret manuel :</p>
                    <code className="text-sm font-mono text-gray-900 break-all">{twoFactorSecret}</code>
                  </div>
                </div>
              )}
            </div>

            {/* Verify 2FA */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">2. Vérifier et Activer 2FA</h3>
              <p className="text-sm text-gray-600 mb-3">
                <code className="bg-gray-100 px-2 py-1 rounded">POST /api/2fa/verify</code>
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Code 6 chiffres"
                  maxLength={6}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-center text-lg"
                />
                <button
                  onClick={handleVerify2FA}
                  disabled={loading2FA || verificationCode.length !== 6}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Vérifier
                </button>
              </div>
            </div>

            {/* Disable 2FA */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">3. Désactiver 2FA</h3>
              <p className="text-sm text-gray-600 mb-3">
                <code className="bg-gray-100 px-2 py-1 rounded">POST /api/2fa/disable</code>
              </p>
              <div className="flex gap-3">
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <button
                  onClick={handleDisable2FA}
                  disabled={loading2FA || !disablePassword.trim()}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Désactiver 2FA
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Section Archivage Projet */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Archive className="w-5 h-5 text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Archivage de projet</h2>
          </div>

          <div className="space-y-4">
            {/* Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-900">
                <p className="font-semibold mb-1">Note importante :</p>
                <p>Pour tester, vous devez d'abord créer un projet ou utiliser un ID de projet existant.</p>
              </div>
            </div>

            {/* Input Project ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID du projet (UUID)
              </label>
              <input
                type="text"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="7448c44f-e9d8-47b8-befc-f56bde23d396"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono text-sm"
              />
            </div>

            {/* Archive */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">1. Archiver un projet</h3>
              <p className="text-sm text-gray-600 mb-3">
                <code className="bg-gray-100 px-2 py-1 rounded">POST /api/projects/:id/archive</code>
              </p>
              <button
                onClick={handleArchiveProject}
                disabled={loadingArchive || !projectId.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                {loadingArchive ? 'Archivage...' : 'Archiver le projet'}
              </button>
            </div>

            {/* Unarchive */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">2. Désarchiver un projet</h3>
              <p className="text-sm text-gray-600 mb-3">
                <code className="bg-gray-100 px-2 py-1 rounded">POST /api/projects/:id/unarchive</code>
              </p>
              <button
                onClick={handleUnarchiveProject}
                disabled={loadingArchive || !projectId.trim()}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {loadingArchive ? 'Désarchivage...' : 'Désarchiver le projet'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
